import { useUserStore } from '@/stores/user'
import { useMembershipStore } from '@/stores/membership'
import { getMyMenuPermissions } from '@/api/menus'

// ⚠️ 关键：缓存必须放在模块作用域（而非 useMenuPermission 内部），
// 否则每个调用方都会得到一份独立缓存，导致 /menus 接口被并发重复请求多次。
const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存
let _permissionCache = null
let _permissionCacheTime = 0
let _permissionInflight = null // 进行中的请求 Promise，用于并发去重

/**
 * 获取用户的所有权限（模块级单例缓存 + 并发去重）。
 * - 命中有效缓存：直接返回，不发请求。
 * - 已有进行中请求：复用同一个 Promise，避免同一时刻打出多条 /menus。
 */
async function fetchUserPermissionsShared() {
  const now = Date.now()

  if (_permissionCache && now - _permissionCacheTime < CACHE_DURATION) {
    return _permissionCache
  }

  if (_permissionInflight) {
    return _permissionInflight
  }

  _permissionInflight = (async () => {
    try {
      const response = await getMyMenuPermissions({ menu_type: null }) // 获取所有类型(包括permission)
      if (response.code === 200 && response.data) {
        _permissionCache = response.data || []
        _permissionCacheTime = Date.now()
        return _permissionCache
      }
      console.error('[权限检查] API返回错误:', response.message)
      return _permissionCache || []
    } catch (error) {
      console.error('[权限检查] 获取用户权限失败:', error)
      return _permissionCache || []
    } finally {
      _permissionInflight = null
    }
  })()

  return _permissionInflight
}

/**
 * 统一菜单权限检查 Composable
 * 替代原有的 useBalanceCheck 中的会员等级检查
 * 所有功能权限通过菜单权限系统统一管理
 */
export function useMenuPermission() {
  const userStore = useUserStore()

  /**
   * 获取用户的所有权限
   * @returns {Promise<Array>} 权限列表
   */
  const fetchUserPermissions = () => fetchUserPermissionsShared()
  
  /**
   * 检查是否拥有指定权限
   * @param {string} permissionCode - 权限代码 (如 'knowledge_base:access')
   * @returns {Promise<boolean>} 是否拥有权限
   */
  const hasPermission = async (permissionCode) => {
    const permissions = await fetchUserPermissions()
    const hasP = permissions.some(p => 
      p.permission_code === permissionCode && p.can_view === true
    )
    console.log(`[权限检查] ${permissionCode}: ${hasP ? '✅ 有权限' : '❌ 无权限'}`)
    return hasP
  }
  
  /**
   * 检查是否拥有任一权限
   * @param {Array<string>} permissionCodes - 权限代码列表
   * @returns {Promise<boolean>} 是否拥有任一权限
   */
  const hasAnyPermission = async (permissionCodes) => {
    for (const code of permissionCodes) {
      if (await hasPermission(code)) {
        return true
      }
    }
    return false
  }
  
  // ==================== 知识库相关权限 ====================
  
  /**
   * 检查知识库访问权限
   * @returns {Promise<boolean>} 是否有权限访问知识库
   */
  const checkKnowledgeBaseAccess = async () => {
    return await hasPermission('knowledge_base:access')
  }
  
  /**
   * 检查知识库文件上传权限
   * @returns {Promise<boolean>} 是否有权限上传文件
   */
  const checkKnowledgeBaseUpload = async () => {
    return await hasPermission('knowledge_base:upload')
  }
  
  /**
   * 情报订阅菜单权限（不含会员等级，与 useIntelligenceSubscriptionAccess 配合）
   */
  const checkIntelligenceSubscriptionPermission = async () => {
    return await hasPermission('intelligence_subscription:use')
  }

  const checkIntelligenceFeedPermission = async () => {
    return await hasPermission('intelligence_feed:view')
  }

  const checkReportInteractiveEditPermission = async () => {
    return await hasPermission('report_interactive_edit:use')
  }
  const checkReportHallucinationPermission = async () => {
    return await hasPermission('report_hallucination_detection:use')
  }
  const checkReportSelectionQaPermission = async () => {
    return await hasPermission('report_selection_qa:use')
  }
  const checkReportSelectionRegeneratePermission = async () => {
    return await hasPermission('report_selection_regenerate:use')
  }
  
  // ==================== 知识库CRUD权限 (新增) ====================
  
  /**
   * 检查创建知识库权限
   * @returns {Promise<boolean>} 是否有权限创建知识库
   */
  const checkKnowledgeCreatePermission = async () => {
    return await hasAnyPermission(['knowledge:create', 'knowledge_base:access'])
  }
  
  /**
   * 检查编辑知识库权限
   * @returns {Promise<boolean>} 是否有权限编辑知识库
   */
  const checkKnowledgeEditPermission = async () => {
    return await hasPermission('knowledge:edit')
  }
  
  /**
   * 检查删除知识库权限
   * @returns {Promise<boolean>} 是否有权限删除知识库
   */
  const checkKnowledgeDeletePermission = async () => {
    return await hasPermission('knowledge:delete')
  }
  
  /**
   * 检查上传知识库文件权限
   * @returns {Promise<boolean>} 是否有权限上传知识库文件
   */
  const checkKnowledgeUploadPermission = async () => {
    return await hasAnyPermission(['knowledge:upload', 'knowledge_base:upload'])
  }
  
  /**
   * 检查导入知识库权限
   * @returns {Promise<boolean>} 是否有权限导入知识库
   */
  const checkKnowledgeImportPermission = async () => {
    return await hasPermission('knowledge:import')
  }
  
  /**
   * 获取知识库最大文件数（来自会员等级，与配额服务一致）
   * @returns {Promise<number>} 最大文件数；0 禁止；9999 表示不限（tier 为 null）
   */
  const getKnowledgeBaseMaxFiles = async () => {
    const membershipStore = useMembershipStore()
    if (!membershipStore.tier) {
      await membershipStore.fetchMembership()
    }
    const limit = membershipStore.tier?.knowledge_base_max_files
    if (limit === null || limit === undefined) {
      return 9999
    }
    const parsed = Number(limit)
    return Number.isFinite(parsed) ? Math.max(Math.trunc(parsed), 0) : 0
  }
  
  // ==================== 批量分析权限 ====================
  
  /**
   * 检查批量分析权限
   * @returns {Promise<boolean>} 是否有权限使用批量分析
   */
  const checkBatchAnalysisAccess = async () => {
    return await hasPermission('batch_analysis:access')
  }
  
  // ==================== 数据导出权限 ====================
  
  /**
   * 检查数据导出权限
   * @returns {Promise<boolean>} 是否有权限导出数据
   */
  const checkDataExportAccess = async () => {
    return await hasPermission('data:export')
  }
  
  // ==================== 高级功能权限 ====================
  
  /**
   * 检查高级功能权限
   * @returns {Promise<boolean>} 是否有权限使用高级功能
   */
  const checkAdvancedFeaturesAccess = async () => {
    return await hasPermission('advanced:access')
  }
  
  // ==================== 文件管理权限 ====================
  
  /**
   * 检查文件查看权限
   * @returns {Promise<boolean>} 是否有权限查看文件列表
   */
  const checkFileViewPermission = async () => {
    return await hasPermission('file:list:view')
  }
  
  /**
   * 检查文件下载权限
   * @returns {Promise<boolean>} 是否有权限下载文件
   */
  const checkFileDownloadPermission = async () => {
    return await hasPermission('file:download')
  }
  
  /**
   * 检查文件删除权限
   * @returns {Promise<boolean>} 是否有权限删除文件
   */
  const checkFileDeletePermission = async () => {
    return await hasPermission('file:delete')
  }
  
  /**
   * 检查文件预览权限
   * @returns {Promise<boolean>} 是否有权限预览文件
   */
  const checkFilePreviewPermission = async () => {
    return await hasPermission('file:preview')
  }
  
  // ==================== 菜单显示控制 ====================
  
  /**
   * 检查菜单是否可见
   * @param {string} menuName - 菜单名称 (如 'KnowledgeBase')
   * @returns {Promise<boolean>} 菜单是否可见
   */
  const isMenuVisible = async (menuName) => {
    const permissions = await fetchUserPermissions()
    const menu = permissions.find(p => p.menu_name === menuName)
    const isVisible = menu && menu.can_view === true
    console.log(`[权限检查] 菜单 ${menuName}: ${isVisible ? '✅ 可见' : '❌ 不可见'}`)
    return isVisible
  }
  
  /**
   * 检查菜单是否可编辑
   * @param {string} menuName - 菜单名称
   * @returns {Promise<boolean>} 菜单是否可编辑
   */
  const isMenuEditable = async (menuName) => {
    const permissions = await fetchUserPermissions()
    const menu = permissions.find(p => p.menu_name === menuName)
    return menu && menu.can_edit === true
  }
  
  // ==================== 工具方法 ====================
  
  /**
   * 清除权限缓存
   * 用于用户登录/登出或权限变更时调用
   */
  const clearPermissionCache = () => {
    console.log('[权限检查] 清除权限缓存')
    _permissionCache = null
    _permissionCacheTime = 0
    _permissionInflight = null
  }
  
  /**
   * 获取用户权限摘要 (调试用)
   * @returns {Promise<Object>} 权限摘要信息
   */
  const getPermissionSummary = async () => {
    const permissions = await fetchUserPermissions()
    
    const permissionCodes = permissions
      .filter(p => p.can_view)
      .map(p => p.permission_code)
      .filter(Boolean)
    
    return {
      userId: userStore.userInfo?.id,
      username: userStore.userInfo?.username,
      totalPermissions: permissions.length,
      permissionCodes: permissionCodes,
      knowledgeBase: {
        canAccess: await checkKnowledgeBaseAccess(),
        canUpload: await checkKnowledgeBaseUpload(),
        maxFiles: await getKnowledgeBaseMaxFiles()
      },
      fileManagement: {
        canView: await checkFileViewPermission(),
        canDownload: await checkFileDownloadPermission(),
        canDelete: await checkFileDeletePermission(),
        canPreview: await checkFilePreviewPermission()
      },
      batchAnalysis: await checkBatchAnalysisAccess(),
      dataExport: await checkDataExportAccess(),
      advancedFeatures: await checkAdvancedFeaturesAccess()
    }
  }
  
  return {
    // 基础权限检查
    hasPermission,
    hasAnyPermission,
    fetchUserPermissions,
    
    // 知识库权限
    checkKnowledgeBaseAccess,
    checkKnowledgeBaseUpload,
    getKnowledgeBaseMaxFiles,
    checkIntelligenceSubscriptionPermission,
    checkIntelligenceFeedPermission,
    checkReportInteractiveEditPermission,
    checkReportHallucinationPermission,
    checkReportSelectionQaPermission,
    checkReportSelectionRegeneratePermission,
    
    // 知识库CRUD权限 (新增)
    checkKnowledgeCreatePermission,
    checkKnowledgeEditPermission,
    checkKnowledgeDeletePermission,
    checkKnowledgeUploadPermission,
    checkKnowledgeImportPermission,
    
    // 文件管理权限
    checkFileViewPermission,
    checkFileDownloadPermission,
    checkFileDeletePermission,
    checkFilePreviewPermission,
    
    // 其他功能权限
    checkBatchAnalysisAccess,
    checkDataExportAccess,
    checkAdvancedFeaturesAccess,
    
    // 菜单控制
    isMenuVisible,
    isMenuEditable,
    
    // 工具方法
    clearPermissionCache,
    getPermissionSummary
  }
}
